/* ==========================================================================
   Baseline theme
   No framework, no jQuery. Deferred, so the DOM is parsed before this runs.
   ========================================================================== */
(function () {
  'use strict';

  var t = window.theme || {};
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- utils */

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function announce(msg) {
    var live = document.getElementById('a11y-live');
    if (live) { live.textContent = ''; window.setTimeout(function () { live.textContent = msg; }, 60); }
  }

  function formatMoney(cents) {
    var fmt = t.moneyFormat || '&euro;{{amount}}';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, parts.join('.'))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, parts[0])
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','));
  }

  /* Constraint 6: one rAF throttled scroll handler. Subscribers never read
     layout inside the raw scroll event. */
  var scrollSubs = [];
  var ticking = false;
  function onScrollFrame() {
    var y = window.pageYOffset;
    for (var i = 0; i < scrollSubs.length; i++) {
      try { scrollSubs[i](y); } catch (e) { /* one bad subscriber must not stop the rest */ }
    }
    ticking = false;
  }
  window.addEventListener('scroll', function () {
    if (!ticking) { ticking = true; window.requestAnimationFrame(onScrollFrame); }
  }, { passive: true });
  function onScroll(fn) { scrollSubs.push(fn); fn(window.pageYOffset); }

  /* ------------------------------------------------------------- reveals
     Constraint 5: requestAnimationFrame is paused in background tabs, so
     above the fold content must not depend on it. IntersectionObserver
     fires on its own, and a timed fallback force reveals anything left. */
  function initReveals() {
    var els = $$('.reveal, .mask-reveal');
    if (!els.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    els.forEach(function (el) { io.observe(el); });

    /* Constraint 4: nothing stays invisible because an observer misfired. */
    window.setTimeout(function () {
      els.forEach(function (el) { el.classList.add('is-in'); });
    }, 2500);
  }

  /* ------------------------------------------------------------- parallax */
  function initParallax() {
    if (reduceMotion) return;
    var layers = $$('[data-parallax]');
    if (!layers.length) return;
    var cache = layers.map(function (el) {
      return { el: el, speed: parseFloat(el.getAttribute('data-parallax')) || 0.15, top: 0, h: 0 };
    });
    function measure() {
      cache.forEach(function (item) {
        var r = item.el.getBoundingClientRect();
        item.top = r.top + window.pageYOffset;
        item.h = r.height;
      });
    }
    measure();
    window.addEventListener('resize', measure);
    onScroll(function (y) {
      var vh = window.innerHeight;
      cache.forEach(function (item) {
        if (item.top + item.h < y || item.top > y + vh) return;
        var offset = (y - item.top) * item.speed;
        item.el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
      });
    });
  }

  /* --------------------------------------------------------------- video
     Constraint 3: load() aborts an in flight play() promise, and autoplay is
     blocked unless muted is set as a property. Wait for canplay. */
  function initHeroVideo() {
    $$('video[data-autoplay]').forEach(function (video) {
      video.muted = true;
      video.playsInline = true;
      if (reduceMotion) { video.removeAttribute('autoplay'); return; }
      var started = false;
      function tryPlay() {
        if (started) return;
        started = true;
        var p = video.play();
        if (p && typeof p.catch === 'function') {
          p.catch(function () { video.controls = false; });
        }
      }
      if (video.readyState >= 3) { tryPlay(); }
      else { video.addEventListener('canplay', tryPlay, { once: true }); }
    });
  }

  /* ------------------------------------------------------- focus trapping */
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  var trapState = null;

  function trapFocus(container, opener) {
    releaseTrap(false);
    var nodes = $$(FOCUSABLE, container).filter(function (n) { return n.offsetParent !== null; });
    if (!nodes.length) return;

    function handler(e) {
      if (e.key === 'Escape') { e.preventDefault(); releaseTrap(true); return; }
      if (e.key !== 'Tab') return;
      var first = nodes[0], last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    trapState = { container: container, opener: opener, handler: handler };
    document.addEventListener('keydown', handler);
    nodes[0].focus();
  }

  function releaseTrap(returnFocus) {
    if (!trapState) return;
    document.removeEventListener('keydown', trapState.handler);
    var opener = trapState.opener;
    var container = trapState.container;
    trapState = null;
    if (container && container.__close) container.__close();
    if (returnFocus && opener && typeof opener.focus === 'function') opener.focus();
  }

  /* --------------------------------------------------------------- drawer */
  function makeDrawer(drawer, overlay) {
    if (!drawer) return null;
    var api = {
      open: function (opener) {
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        if (overlay) overlay.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        trapFocus(drawer, opener);
      },
      close: function () {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        if (overlay) overlay.classList.remove('is-open');
        document.body.style.overflow = '';
      }
    };
    drawer.__close = api.close;
    if (overlay) overlay.addEventListener('click', function () { releaseTrap(true); });
    $$('[data-drawer-close]', drawer).forEach(function (btn) {
      btn.addEventListener('click', function () { releaseTrap(true); });
    });
    return api;
  }

  /* ----------------------------------------------------------------- cart */
  var cartDrawer = null;
  var cartOverlay = null;
  var cartApi = null;

  function renderCartSections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach(function (id) {
      var target = document.getElementById('shopify-section-' + id) || $('[data-section-id="' + id + '"]');
      if (!target) return;
      var parsed = new DOMParser().parseFromString(sections[id], 'text/html');
      var fresh = parsed.getElementById('shopify-section-' + id) || parsed.querySelector('[data-section-id="' + id + '"]');
      if (fresh) target.innerHTML = fresh.innerHTML;
    });
    syncCartCount();
    bindCartControls();
  }

  function syncCartCount() {
    var source = $('[data-cart-count-source]');
    if (!source) return;
    var count = parseInt(source.getAttribute('data-cart-count-source'), 10) || 0;
    $$('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  function cartSectionIds() {
    var ids = [];
    $$('[data-cart-section]').forEach(function (el) {
      var id = el.getAttribute('data-cart-section');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function addToCart(form, button) {
    var data = new FormData(form);
    var ids = cartSectionIds();
    if (ids.length) data.append('sections', ids.join(','));
    if (button) button.classList.add('is-loading');

    return fetch(t.routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: data
    })
      .then(function (r) { return r.json(); })
      .then(function (json) {
        if (json.status) {
          announce(json.description || json.message || t.strings.error);
          return;
        }
        renderCartSections(json.sections);
        announce(t.strings.added);
        if (cartApi && $('[data-cart-drawer]')) cartApi.open(button);
      })
      .catch(function () { announce(t.strings.error); })
      .finally(function () { if (button) button.classList.remove('is-loading'); });
  }

  function changeLine(key, quantity, row) {
    if (row) row.classList.add('is-updating');
    return fetch(t.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity, sections: cartSectionIds().join(',') })
    })
      .then(function (r) { return r.json(); })
      .then(function (json) { renderCartSections(json.sections); })
      .catch(function () { announce(t.strings.error); if (row) row.classList.remove('is-updating'); });
  }

  function bindCartControls() {
    $$('[data-qty]').forEach(function (widget) {
      if (widget.__bound) return;
      widget.__bound = true;
      var input = $('input', widget);
      widget.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-qty-step]');
        if (!btn || !input) return;
        var step = parseInt(btn.getAttribute('data-qty-step'), 10);
        var next = Math.max(parseInt(input.min || '0', 10), (parseInt(input.value, 10) || 0) + step);
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      if (input) {
        input.addEventListener('change', function () {
          var key = input.getAttribute('data-line-key');
          if (key) changeLine(key, parseInt(input.value, 10) || 0, input.closest('.line-item'));
        });
      }
    });

    $$('[data-line-remove]').forEach(function (btn) {
      if (btn.__bound) return;
      btn.__bound = true;
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        changeLine(btn.getAttribute('data-line-remove'), 0, btn.closest('.line-item'));
      });
    });

    updateShipBar();
  }

  function updateShipBar() {
    var bar = $('[data-ship-bar]');
    if (!bar) return;
    var threshold = t.freeShippingThreshold || 0;
    if (threshold <= 0) { bar.hidden = true; return; }
    var total = parseInt(bar.getAttribute('data-cart-total'), 10) || 0;
    var pct = Math.min(100, (total / threshold) * 100);
    var fill = $('.ship-bar__fill', bar);
    var label = $('[data-ship-label]', bar);
    if (fill) fill.style.width = pct + '%';
    if (label) {
      label.textContent = total >= threshold
        ? label.getAttribute('data-reached')
        : label.getAttribute('data-remaining').replace('__AMT__', formatMoney(threshold - total));
    }
  }

  /* ------------------------------------------------------ predictive search */
  function initSearch() {
    var wrap = $('[data-search]');
    if (!wrap) return;
    var input = $('input[type="search"]', wrap);
    var results = $('[data-search-results]', wrap);
    if (!input || !results) return;
    var timer = null;
    var controller = null;

    input.addEventListener('input', function () {
      var q = input.value.trim();
      window.clearTimeout(timer);
      if (controller) controller.abort();
      if (q.length < 2) { results.innerHTML = ''; results.hidden = true; return; }

      timer = window.setTimeout(function () {
        controller = new AbortController();
        var url = t.routes.predictive_search_url +
          '?q=' + encodeURIComponent(q) +
          '&resources[type]=product,collection,page' +
          '&resources[limit]=6' +
          '&section_id=predictive-search';
        fetch(url, { signal: controller.signal })
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var section = parsed.querySelector('[data-predictive-results]');
            results.innerHTML = section ? section.innerHTML : '';
            results.hidden = false;
          })
          .catch(function (err) { if (err.name !== 'AbortError') { results.hidden = true; } });
      }, 220);
    });

    document.addEventListener('click', function (e) {
      if (!wrap.contains(e.target)) { results.hidden = true; }
    });
  }

  /* ------------------------------------------- collection facets and sort */
  function initFacets() {
    var form = $('[data-facet-form]');
    var grid = $('[data-collection-grid]');
    if (!form || !grid) return;

    function showSkeletons() {
      var count = Math.min(grid.children.length || 8, 12);
      var html = '';
      for (var i = 0; i < count; i++) {
        html += '<div class="skeleton-card" aria-hidden="true">' +
                '<div class="skeleton skeleton-card__media"></div>' +
                '<div class="skeleton skeleton-card__line"></div>' +
                '<div class="skeleton skeleton-card__line skeleton-card__line--short"></div>' +
                '</div>';
      }
      grid.innerHTML = html;
    }

    function apply(url, push) {
      grid.classList.add('is-updating');
      showSkeletons();
      var sectionId = grid.getAttribute('data-section-id');
      fetch(url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + sectionId)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var freshGrid = parsed.querySelector('[data-collection-grid]');
          var freshFacets = parsed.querySelector('[data-facet-form]');
          if (freshGrid) grid.innerHTML = freshGrid.innerHTML;
          if (freshFacets) form.innerHTML = freshFacets.innerHTML;
          if (push) window.history.pushState({ url: url }, '', url);
          grid.classList.remove('is-updating');
          announce(grid.getAttribute('data-updated-message') || '');
          initReveals();
        })
        .catch(function () { window.location.href = url; });
    }

    function buildUrl() {
      var params = new URLSearchParams(new FormData(form));
      var query = [];
      params.forEach(function (value, key) { if (value) query.push(key + '=' + encodeURIComponent(value)); });
      var sort = $('[data-sort]');
      if (sort && sort.value) query.push('sort_by=' + encodeURIComponent(sort.value));
      return form.getAttribute('action') + (query.length ? '?' + query.join('&') : '');
    }

    form.addEventListener('change', function () { apply(buildUrl(), true); });

    /* The sort control lives in the toolbar, outside the facet form, so its
       change event never bubbles to the form. Wire it to the same path rather
       than letting it fall back to a full page navigation. */
    var sortSelect = $('[data-sort]');
    if (sortSelect) {
      sortSelect.addEventListener('change', function () { apply(buildUrl(), true); });
    }

    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-facet-link]');
      if (!link) return;
      e.preventDefault();
      apply(link.getAttribute('href'), true);
    });

    window.addEventListener('popstate', function () { apply(window.location.href, false); });
  }

  /* ----------------------------------------------------------- accordions */
  function initAccordions() {
    $$('.accordion__trigger').forEach(function (trigger) {
      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;
      if (trigger.getAttribute('aria-expanded') === 'true') panel.style.height = 'auto';
      trigger.addEventListener('click', function () {
        var open = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!open));
        if (open) {
          panel.style.height = panel.scrollHeight + 'px';
          window.requestAnimationFrame(function () { panel.style.height = '0px'; });
        } else {
          panel.style.height = panel.scrollHeight + 'px';
          panel.addEventListener('transitionend', function once() {
            panel.style.height = 'auto';
            panel.removeEventListener('transitionend', once);
          });
        }
      });
    });
  }

  /* -------------------------------------------------------------- gallery */
  function initGallery() {
    var gallery = $('[data-gallery]');
    if (!gallery) return;
    var main = $('[data-gallery-main]', gallery);
    var mainImg = main ? $('img', main) : null;

    $$('[data-gallery-thumb]', gallery).forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        if (!mainImg) return;
        mainImg.src = thumb.getAttribute('data-full');
        mainImg.srcset = thumb.getAttribute('data-srcset') || '';
        mainImg.alt = thumb.getAttribute('data-alt') || '';
        $$('[data-gallery-thumb]', gallery).forEach(function (n) { n.setAttribute('aria-current', 'false'); });
        thumb.setAttribute('aria-current', 'true');
        if (main) main.classList.remove('is-zoomed');
      });
    });

    if (main) {
      main.addEventListener('click', function () { main.classList.toggle('is-zoomed'); });
    }
  }

  /* ------------------------------------------------------ sticky buy bar */
  function initBuyBar() {
    var bar = $('[data-buy-bar]');
    var anchor = $('[data-buy-anchor]');
    if (!bar || !anchor) return;
    var anchorBottom = 0;
    function measure() { anchorBottom = anchor.getBoundingClientRect().bottom + window.pageYOffset; }
    measure();
    window.addEventListener('resize', measure);
    onScroll(function (y) { bar.classList.toggle('is-visible', y > anchorBottom); });
  }

  /* ------------------------------------------------- magnetic buttons */
  function initMagnetic() {
    if (reduceMotion) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    $$('[data-magnetic]').forEach(function (btn) {
      btn.addEventListener('pointermove', function (e) {
        var r = btn.getBoundingClientRect();
        var x = (e.clientX - r.left - r.width / 2) * 0.18;
        var y = (e.clientY - r.top - r.height / 2) * 0.28;
        btn.style.transform = 'translate(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px)';
      });
      btn.addEventListener('pointerleave', function () { btn.style.transform = ''; });
    });
  }

  /* ------------------------------------------------------ recently viewed */
  function initRecentlyViewed() {
    var KEY = 'te:recently-viewed';
    var current = document.body.getAttribute('data-product-handle');
    var list = [];
    try { list = JSON.parse(window.localStorage.getItem(KEY) || '[]'); } catch (e) { list = []; }

    if (current) {
      list = list.filter(function (h) { return h !== current; });
      list.unshift(current);
      list = list.slice(0, 8);
      try { window.localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) { /* private mode */ }
    }

    var holder = $('[data-recently-viewed]');
    if (!holder) return;
    var handles = list.filter(function (h) { return h !== current; }).slice(0, 4);
    if (!handles.length) { holder.hidden = true; return; }

    var q = handles.map(function (h) { return 'handle:' + h; }).join(' OR ');
    fetch('/search?q=' + encodeURIComponent(q) + '&type=product&section_id=recently-viewed')
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var inner = parsed.querySelector('[data-recently-viewed-inner]');
        if (inner && inner.children.length) { holder.innerHTML = inner.innerHTML; holder.hidden = false; }
        else { holder.hidden = true; }
      })
      .catch(function () { holder.hidden = true; });
  }

  /* ----------------------------------------------------------- disclosure */
  function initDisclosures() {
    $$('[data-disclosure]').forEach(function (trigger) {
      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;
      trigger.addEventListener('click', function () {
        var open = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!open));
        panel.hidden = open;
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && trigger.getAttribute('aria-expanded') === 'true') {
          trigger.setAttribute('aria-expanded', 'false');
          panel.hidden = true;
          trigger.focus();
        }
      });
    });
  }

  /* ---------------------------------------------------------------- modal */
  function initModals() {
    $$('[data-modal-open]').forEach(function (trigger) {
      var modal = document.getElementById(trigger.getAttribute('data-modal-open'));
      if (!modal) return;
      modal.__close = function () { modal.hidden = true; document.body.style.overflow = ''; };
      trigger.addEventListener('click', function () {
        modal.hidden = false;
        document.body.style.overflow = 'hidden';
        trapFocus(modal, trigger);
      });
      $$('[data-modal-close]', modal).forEach(function (btn) {
        btn.addEventListener('click', function () { releaseTrap(true); });
      });
      modal.addEventListener('click', function (e) { if (e.target === modal) releaseTrap(true); });
    });
  }

  /* -------------------------------------------------------- gallery swipe
     Scroll snap does the gesture, this only keeps the thumb state in sync so
     the two controls never disagree about which image is showing. */
  function initSwipe() {
    var wrap = $('[data-gallery-swipe]');
    if (!wrap) return;
    var main = $('[data-gallery-main]', wrap);
    if (!main) return;
    var thumbs = $$('[data-gallery-thumb]', wrap);
    if (thumbs.length < 2) return;

    thumbs.forEach(function (thumb, i) {
      if (i === 0) return;
      var img = new Image();
      img.src = thumb.getAttribute('data-full');
      img.alt = thumb.getAttribute('data-alt') || '';
      img.loading = 'lazy';
      main.appendChild(img);
    });

    var syncing = false;
    main.addEventListener('scroll', function () {
      if (syncing) return;
      syncing = true;
      window.requestAnimationFrame(function () {
        var index = Math.round(main.scrollLeft / main.clientWidth);
        thumbs.forEach(function (n, i) { n.setAttribute('aria-current', String(i === index)); });
        syncing = false;
      });
    }, { passive: true });
  }

  /* ------------------------------------------------------------------ boot */
  function boot() {
    cartDrawer = $('[data-cart-drawer]');
    cartOverlay = $('[data-overlay]');
    cartApi = makeDrawer(cartDrawer, cartOverlay);

    var menuDrawer = $('[data-menu-drawer]');
    var menuApi = makeDrawer(menuDrawer, cartOverlay);

    $$('[data-cart-open]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        if (!cartApi) return;
        e.preventDefault();
        cartApi.open(btn);
      });
    });
    $$('[data-menu-open]').forEach(function (btn) {
      btn.addEventListener('click', function (e) { if (menuApi) { e.preventDefault(); menuApi.open(btn); } });
    });

    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[data-product-form]');
      if (!form) return;
      e.preventDefault();
      addToCart(form, $('[data-add-to-cart]', form));
    });

    $$('[data-mega]').forEach(function (trigger) {
      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;
      var wrap = trigger.closest('.mega');
      function open(state) {
        trigger.setAttribute('aria-expanded', String(state));
        panel.hidden = !state;
      }
      trigger.addEventListener('click', function () { open(trigger.getAttribute('aria-expanded') !== 'true'); });
      if (wrap) {
        wrap.addEventListener('mouseenter', function () { if (window.matchMedia('(pointer: fine)').matches) open(true); });
        wrap.addEventListener('mouseleave', function () { if (window.matchMedia('(pointer: fine)').matches) open(false); });
      }
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') open(false); });
    });

    initReveals();
    initParallax();
    initHeroVideo();
    initSearch();
    initFacets();
    initAccordions();
    initGallery();
    initBuyBar();
    initMagnetic();
    initDisclosures();
    initModals();
    initSwipe();
    initRecentlyViewed();
    bindCartControls();
    syncCartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
